<template>
  <b-card class="wallet-table-card">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h5 class="mb-0">
        Movimientos globales
      </h5>
      <div>
        <b-button
          size="sm"
          variant="outline-primary"
          @click="$emit('refresh')"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-1"
          />
          Refrescar
        </b-button>
      </div>
    </div>

    <div class="d-flex flex-wrap align-items-end mb-1">
      <b-form-select
        v-model="filters.type"
        size="sm"
        class="mr-1 mb-1"
        :options="typeOptions"
      />
      <b-form-input
        v-model="filters.q"
        size="sm"
        class="mr-1 mb-1"
        placeholder="Buscar cliente, nota, referencia…"
      />
      <b-button
        size="sm"
        variant="outline-secondary"
        class="mb-1"
        @click="resetFilters"
      >
        Limpiar
      </b-button>
      <div class="text-muted small ml-auto mb-1">
        Mostrando {{ filteredItems.length }} de {{ displayTotal }}
      </div>
    </div>

    <b-table
      :items="filteredItems"
      :fields="fields"
      :busy="loading"
      small
      hover
      responsive
      head-variant="light"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :empty-text="loading ? 'Cargando…' : 'Sin movimientos'"
    >
      <template #cell(created_at)="ctx">
        {{ formatDateTime(ctx.item.created_at) }}
      </template>

      <template #cell(customer)="ctx">
        <router-link
          v-if="ctx.item.customer_id"
          class="wallet-link"
          :to="{ name: 'WalletForCustomer', params: { customerId: ctx.item.customer_id } }"
        >
          <div class="font-weight-bold">
            {{ ctx.item.customer_name || '—' }}
          </div>
          <div class="text-muted small">
            {{ ctx.item.customer_email || '—' }}
          </div>
        </router-link>
        <div v-else>
          <div class="font-weight-bold">
            {{ ctx.item.customer_name || '—' }}
          </div>
          <div class="text-muted small">
            {{ ctx.item.customer_email || '—' }}
          </div>
        </div>
      </template>

      <template #cell(reference)="ctx">
        <span v-if="ctx.item.reference_id">
          <router-link
            v-if="String(ctx.item.reference_type || '').toLowerCase() === 'quote'"
            :to="`/orden-de-compra/${ctx.item.reference_id}`"
          >
            Cotización #{{ ctx.item.reference_id }}
          </router-link>
          <span v-else>
            {{ ctx.item.reference_type || 'Referencia' }} #{{ ctx.item.reference_id }}
          </span>
        </span>
        <span
          v-else
          class="text-muted"
        >—</span>
      </template>

      <template #cell(tx_type)="ctx">
        <b-badge :variant="typeVariant(ctx.item)">
          {{ typeLabel(ctx.item) }}
        </b-badge>
      </template>

      <template #cell(amount)="ctx">
        <div :class="amountClass(ctx.item)">
          {{ money(displayAmount(ctx.item)) }}
        </div>
      </template>

      <template #cell(actor)="ctx">
        <div class="font-weight-bold">
          {{ (ctx.item.actor && ctx.item.actor.name) || '—' }}
        </div>
        <div class="text-muted small">
          {{ (ctx.item.actor && ctx.item.actor.role) || '—' }} · {{ (ctx.item.actor && ctx.item.actor.email) || '—' }}
        </div>
      </template>
    </b-table>

    <div class="d-flex justify-content-end">
      <b-pagination
        v-if="pagination.total_objects > pagination.per_page"
        :value="pagination.page"
        :per-page="pagination.per_page"
        :total-rows="pagination.total_objects"
        size="sm"
        class="mt-2"
        @change="$emit('change-page', $event)"
      />
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'WalletAdminTransactionsTable',
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    pagination: {
      type: Object,
      default: () => ({ page: 1, per_page: 100, total_objects: 0 }),
    },
  },
  data() {
    return {
      sortBy: 'created_at',
      sortDesc: true,
      filters: { type: null, q: '' },
      typeOptions: [
        { value: null, text: 'Todos' },
        { value: 'credit', text: 'Abonos' },
        { value: 'debit', text: 'Egresos' },
        { value: 'adjustment', text: 'Ajustes' },
      ],
      fields: [
        {
          key: 'created_at',
          label: 'Fecha',
          sortable: true,
          thClass: 'align-middle',
        },
        {
          key: 'customer',
          label: 'Cliente',
          thClass: 'align-middle',
        },
        {
          key: 'note',
          label: 'Descripción',
          thClass: 'align-middle',
        },
        {
          key: 'reference',
          label: 'Referencia',
          thClass: 'align-middle',
        },
        {
          key: 'tx_type',
          label: 'Tipo',
          sortable: true,
          thClass: 'align-middle',
        },
        {
          key: 'amount',
          label: 'Monto',
          sortable: true,
          thClass: 'text-right align-middle',
          tdClass: 'text-right',
        },
        {
          key: 'actor',
          label: 'Actor',
          thClass: 'align-middle',
        },
      ],
    }
  },
  computed: {
    displayTotal() {
      return this.pagination && this.pagination.total_objects ? this.pagination.total_objects : this.items.length
    },
    filteredItems() {
      const { type, q: rawQ } = this.filters
      const q = String(rawQ || '').trim().toLowerCase()

      return (this.items || []).filter(t => {
        if (type && t.tx_type !== type) return false
        if (!q) return true

        const hay = [
          t.customer_name,
          t.customer_email,
          t.note,
          t.reference_type,
          t.reference_id,
          t.wallet_id,
          t.customer_id,
          t.actor && t.actor.name,
          t.actor && t.actor.email,
          t.actor && t.actor.role,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return hay.includes(q)
      })
    },
  },
  methods: {
    resetFilters() {
      this.filters = { type: null, q: '' }
    },
    money(v) {
      const n = Number(v || 0)
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)
    },
    formatDateTime(v) {
      if (!v) return '—'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return '—'
      return new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(d)
    },
    adjustmentDelta(item) {
      const delta = Number(item && item.metadata && item.metadata.delta)
      if (!Number.isNaN(delta)) return delta
      return Number(item && item.amount) || 0
    },
    displayAmount(item) {
      if (!item) return 0
      if (item.tx_type === 'adjustment') return this.adjustmentDelta(item)
      return Number(item.amount || 0)
    },
    typeLabel(item) {
      const t = item && item.tx_type
      if (t === 'credit') return 'abono'
      if (t === 'debit') return 'egreso'
      if (t === 'adjustment') {
        const delta = this.adjustmentDelta(item)
        if (delta > 0) return 'ajuste +'
        if (delta < 0) return 'ajuste -'
        return 'ajuste'
      }
      return t || '—'
    },
    typeVariant(item) {
      const t = item && item.tx_type
      if (t === 'credit') return 'success'
      if (t === 'debit') return 'danger'
      if (t === 'adjustment') {
        const delta = this.adjustmentDelta(item)
        if (delta > 0) return 'success'
        if (delta < 0) return 'danger'
        return 'warning'
      }
      return 'secondary'
    },
    amountClass(item) {
      const t = item && item.tx_type
      if (t === 'credit') return 'text-success'
      if (t === 'debit') return 'text-danger'
      if (t === 'adjustment') {
        const delta = this.adjustmentDelta(item)
        if (delta > 0) return 'text-success'
        if (delta < 0) return 'text-danger'
        return 'text-warning'
      }
      return ''
    },
  },
}
</script>

<style scoped>
.wallet-link {
  color: inherit;
  text-decoration: none;
}

.wallet-link:hover .font-weight-bold {
  text-decoration: underline;
}

.wallet-table-card {
  border: 1px solid #edf0f4;
  box-shadow: 0 10px 24px rgba(17, 38, 63, 0.06);
}

.wallet-table-card ::v-deep .table thead th {
  background: #f8fafc;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: #8a94a6;
  border-bottom: 1px solid #e9eef3;
}

.wallet-table-card ::v-deep .table tbody tr:hover {
  background: #f3f6fb;
}
</style>
